/**
 * Warp 9 -> Velora texting cut-over (Settings > Texting > Warp 9 hand-off).
 *
 * One owner per (office, message type). While Warp 9 owns a type Velora
 * sends nothing of that type at that office; with shadow mode on it records
 * what it WOULD have sent and a daily report compares that with Warp 9's own
 * send log. Once Velora owns a type, every send still passes the automations
 * master switch, and a 72-hour duplicate guard refuses a text when Warp 9
 * texted the same visit or number in the last 72 hours.
 *
 * Runbook: docs/warp9-cutover.md. The Twilio messaging webhook is the single
 * cut point; pointing it back at Warp 9 is the rollback.
 */
import { z } from 'zod';

/** Message types that switch owner one at a time. */
export const CUTOVER_MESSAGE_TYPES = ['reminders', 'recall', 'missed_call', 'reviews'] as const;
export type CutoverMessageType = (typeof CUTOVER_MESSAGE_TYPES)[number];
export const CutoverMessageTypeSchema = z.enum(CUTOVER_MESSAGE_TYPES);

export const TEXTING_OWNERS = ['warp9', 'velora'] as const;
export type TextingOwner = (typeof TEXTING_OWNERS)[number];
export const TextingOwnerSchema = z.enum(TEXTING_OWNERS);

/** Warp 9 outgoing texts, classified by their template. */
export const WARP9_MESSAGE_TYPES = [...CUTOVER_MESSAGE_TYPES, 'birthday', 'unknown'] as const;
export type Warp9MessageType = (typeof WARP9_MESSAGE_TYPES)[number];

export const CUTOVER_TYPE_LABEL: Readonly<Record<CutoverMessageType, string>> = {
  reminders: 'Reminders and confirmations',
  recall: 'Recall reminders',
  missed_call: 'Missed-call text-back',
  reviews: 'Review requests',
};

/** Hours the duplicate guard looks back into the Warp 9 log. */
export const DUPLICATE_GUARD_HOURS = 72;

/** Why a Velora text was not sent during the cut-over. */
export const CUTOVER_HOLD_REASONS = ['warp9_owner', 'warp9_recent'] as const;
export type CutoverHoldReason = (typeof CUTOVER_HOLD_REASONS)[number];
export const CUTOVER_HOLD_TEXT: Readonly<Record<CutoverHoldReason, string>> = {
  warp9_owner: 'Warp 9 still sends this message type for this office',
  warp9_recent: 'Warp 9 texted this visit or number in the last 72 hours',
};

/* ------------------------------------------------------------------------ */
/* API shapes                                                                */
/* ------------------------------------------------------------------------ */

export const ImportRunSchema = z.object({
  ranAt: z.string(),
  mode: z.enum(['dry_run', 'apply']),
  actorName: z.string(),
  counts: z.record(z.string(), z.number()),
});
export type ImportRun = z.infer<typeof ImportRunSchema>;

export const ShadowTypeCountsSchema = z.object({
  veloraWould: z.number().int(),
  veloraWouldHold: z.number().int(),
  warp9Sent: z.number().int(),
  matched: z.number().int(),
  timingMismatch: z.number().int(),
  copyMismatch: z.number().int(),
  veloraOnly: z.number().int(),
  warp9Only: z.number().int(),
});
export type ShadowTypeCounts = z.infer<typeof ShadowTypeCountsSchema>;

export const ShadowReportCountsSchema = z.record(z.string(), ShadowTypeCountsSchema);
export type ShadowReportCounts = z.infer<typeof ShadowReportCountsSchema>;

export const ShadowMismatchSchema = z.object({
  kind: z.enum(['timing', 'copy', 'velora_only', 'warp9_only']),
  messageType: z.string(),
  veloraId: z.string().nullable(),
  warp9Id: z.string().nullable(),
  veloraAt: z.string().nullable(),
  warp9At: z.string().nullable(),
  /** Hours Velora would have been later (+) or earlier (-) than Warp 9. */
  deltaHours: z.number().nullable(),
  /** 0..1 word overlap of the two texts with names and numbers masked. */
  similarity: z.number().nullable(),
});
export type ShadowMismatch = z.infer<typeof ShadowMismatchSchema>;

export const ShadowReportSchema = z.object({
  reportDate: z.string(),
  windowStart: z.string(),
  windowEnd: z.string(),
  counts: ShadowReportCountsSchema,
  mismatches: z.array(ShadowMismatchSchema),
  generatedAt: z.string(),
});
export type ShadowReport = z.infer<typeof ShadowReportSchema>;

export const OfficeCutoverSchema = z.object({
  officeId: z.string(),
  officeName: z.string(),
  /** Null: the office never used Warp 9, so Velora owns every type. */
  link: z
    .object({
      tenant: z.string(),
      databaseName: z.string(),
      shadowEnabled: z.boolean(),
      shadowStartedAt: z.string().nullable(),
      version: z.number().int(),
    })
    .nullable(),
  owners: z.record(CutoverMessageTypeSchema, TextingOwnerSchema),
  ownerVersions: z.record(CutoverMessageTypeSchema, z.number().int()),
  log: z.object({
    sends: z.number().int(),
    lastSentAt: z.string().nullable(),
    optOutsImported: z.number().int(),
  }),
  lastImport: ImportRunSchema.nullable(),
  shadow: z.object({
    wouldSend: z.number().int(),
    blockedRecent: z.number().int(),
  }),
  latestReport: ShadowReportSchema.nullable(),
  canManage: z.boolean(),
});
export type OfficeCutover = z.infer<typeof OfficeCutoverSchema>;

export const CutoverViewSchema = z.object({
  offices: z.array(OfficeCutoverSchema),
  guardHours: z.number().int(),
  /**
   * Server flag VELORA_WARP9_CUTOVER (default off). Off: shadow and compare
   * only; no type can be handed to Velora and linked offices stay Warp 9's.
   */
  cutoverEnabled: z.boolean(),
});
export type CutoverView = z.infer<typeof CutoverViewSchema>;

export const LinkPutSchema = z.object({
  /** Null removes the link (the office never used Warp 9). */
  tenant: z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,62}$/)
    .nullable(),
  databaseName: z
    .string()
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/)
    .optional(),
  shadowEnabled: z.boolean().optional(),
  expectedVersion: z.number().int().positive(),
});
export type LinkPut = z.infer<typeof LinkPutSchema>;

export const OwnerPutSchema = z.object({
  owner: TextingOwnerSchema,
  expectedVersion: z.number().int().positive(),
  /** Required to give a type to Velora: the switch-day checklist was followed. */
  attestation: z.string().max(400).optional(),
});
export type OwnerPut = z.infer<typeof OwnerPutSchema>;

export const ReportRunSchema = z.object({
  /** Local office date (YYYY-MM-DD); default yesterday. */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

/** The statement a manager confirms before Velora takes a type over. */
export const OWNER_SWITCH_ATTESTATION =
  'The Warp 9 sequence for this message type is off and the Twilio messaging webhook of the office number points at Velora (docs/warp9-cutover.md).';
